import { fetchPublicHubDetails } from './hub-public-details.client';
import {
    HubPreviewBrandingExpectation,
    HubPreviewFooterExpectation,
    HubPreviewRegistrationExpectation,
    HubPreviewRegistrationFieldExpectation,
    HubPreviewSignInExpectation,
    HubPreviewSyncExpectation,
    HubPublicDetailsDTO,
} from './hub-preview-sync.types';

export const HUB_PREVIEW_SYNC_POLL_INTERVAL_MS = 500;
export const HUB_PREVIEW_SYNC_MAX_ATTEMPTS = 20;

const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const normalizeOptionalText = (value: string | null | undefined): string | null => {
    if (value == null) {
        return null;
    }

    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
};

const textMatches = (actual: string | null | undefined, expected: string | null | undefined): boolean =>
    normalizeOptionalText(actual) === normalizeOptionalText(expected);

const imageMatches = (imageUrl: string | null | undefined, objectKey: string | undefined): boolean => {
    const normalizedObjectKey = objectKey?.trim() ?? '';

    if (!normalizedObjectKey) {
        return !normalizeOptionalText(imageUrl);
    }

    return !!imageUrl && imageUrl.includes(normalizedObjectKey);
};

const fieldsMatch = (
    actual: HubPreviewRegistrationFieldExpectation[] | undefined,
    expected: HubPreviewRegistrationFieldExpectation[],
): boolean => {
    if (!actual || actual.length !== expected.length) {
        return false;
    }

    const sortedActual = [...actual].sort((left, right) => left.order - right.order);
    const sortedExpected = [...expected].sort((left, right) => left.order - right.order);

    return sortedExpected.every((expectedField, index) => {
        const actualField = sortedActual[index];

        return actualField.fieldType === expectedField.fieldType
            && textMatches(actualField.label, expectedField.label)
            && actualField.mandatory === expectedField.mandatory
            && actualField.order === expectedField.order;
    });
};

const isBrandingSynced = (
    details: HubPublicDetailsDTO,
    expected: HubPreviewBrandingExpectation,
): boolean => {
    if (expected.primaryColor !== undefined && !textMatches(details.primaryColor, expected.primaryColor)) {
        return false;
    }

    if (expected.logoObjectKey !== undefined && !imageMatches(details.logoUrl, expected.logoObjectKey)) {
        return false;
    }

    if (expected.backgroundObjectKey !== undefined && !imageMatches(details.backgroundUrl, expected.backgroundObjectKey)) {
        return false;
    }

    if (expected.customCss !== undefined && !textMatches(details.customCss, expected.customCss)) {
        return false;
    }

    return true;
};

const isRegistrationSynced = (
    details: HubPublicDetailsDTO,
    expected: HubPreviewRegistrationExpectation,
): boolean => {
    if (expected.registrationEnabled !== undefined && details.registrationEnabled !== expected.registrationEnabled) {
        return false;
    }

    if (expected.registrationDisplayName !== undefined
        && !textMatches(details.registrationDisplayName, expected.registrationDisplayName)) {
        return false;
    }

    if (expected.registrationInstructions !== undefined
        && !textMatches(details.registrationInstructions, expected.registrationInstructions)) {
        return false;
    }

    if (expected.registerButtonLabel !== undefined
        && !textMatches(details.registerButtonLabel, expected.registerButtonLabel)) {
        return false;
    }

    if (expected.registrationSubmittedMessage !== undefined
        && !textMatches(details.registrationSubmittedMessage, expected.registrationSubmittedMessage)) {
        return false;
    }

    return true;
};

const isFooterSynced = (
    details: HubPublicDetailsDTO,
    expected: HubPreviewFooterExpectation,
): boolean => {
    if (expected.footerText !== undefined && !textMatches(details.footerText, expected.footerText)) {
        return false;
    }

    if (expected.showWebsiteFooterLogoImage !== undefined
        && details.showWebsiteFooterLogoImage !== expected.showWebsiteFooterLogoImage) {
        return false;
    }

    return true;
};

const isSignInSynced = (
    details: HubPublicDetailsDTO,
    expected: HubPreviewSignInExpectation,
): boolean => {
    if (expected.signInDisplayName !== undefined
        && !textMatches(details.signInDisplayName, expected.signInDisplayName)) {
        return false;
    }

    if (expected.requestLinkInstructions !== undefined
        && !textMatches(details.requestLinkInstructions, expected.requestLinkInstructions)) {
        return false;
    }

    if (expected.requestLinkButtonLabel !== undefined
        && !textMatches(details.requestLinkButtonLabel, expected.requestLinkButtonLabel)) {
        return false;
    }

    if (expected.signInLinkRequestedMessage !== undefined
        && !textMatches(details.signInLinkRequestedMessage, expected.signInLinkRequestedMessage)) {
        return false;
    }

    return true;
};

export const isHubPreviewSynced = (
    details: HubPublicDetailsDTO,
    expectation: HubPreviewSyncExpectation,
): boolean => {
    if (expectation.branding && !isBrandingSynced(details, expectation.branding)) {
        return false;
    }

    if (expectation.registration && !isRegistrationSynced(details, expectation.registration)) {
        return false;
    }

    if (expectation.registrationFields && !fieldsMatch(details.fields, expectation.registrationFields)) {
        return false;
    }

    if (expectation.footer && !isFooterSynced(details, expectation.footer)) {
        return false;
    }

    if (expectation.signIn && !isSignInSynced(details, expectation.signIn)) {
        return false;
    }

    return true;
};

export const waitForHubPreviewSync = async (
    subdomain: string,
    expectation: HubPreviewSyncExpectation,
    fetchDetails: (subdomain: string) => Promise<HubPublicDetailsDTO> = fetchPublicHubDetails,
): Promise<boolean> => {
    for (let attempt = 0; attempt < HUB_PREVIEW_SYNC_MAX_ATTEMPTS; attempt += 1) {
        try {
            const details = await fetchDetails(subdomain);
            if (isHubPreviewSynced(details, expectation)) {
                return true;
            }
        } catch {
            // Keep polling until Hub BE has the saved values or attempts run out.
        }

        if (attempt < HUB_PREVIEW_SYNC_MAX_ATTEMPTS - 1) {
            await sleep(HUB_PREVIEW_SYNC_POLL_INTERVAL_MS);
        }
    }

    return false;
};
